import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, AlertTriangle } from 'lucide-react';
import { planApi } from '../api/tenants.js';
import { useTenant } from '../components/TenantContext.jsx';
import { PageHeader } from '../components/ui/PageHeader.jsx';
import { Card } from '../components/ui/Card.jsx';
import { MinuteRuleNotes, daysUntil } from '../components/MinuteQuota.jsx';

/** /dashboard/minutes — 分鐘數紀錄：每一批加購分鐘的灌入日與到期日（先扣最快到期的那包） */
const SOON_DAYS = 30;   // 剩這麼多天內到期 → 黃色提醒
const num = (n) => Number(n || 0).toLocaleString();

export default function MinuteRecordsPage() {
    const { t } = useTranslation('dashboard');
    const nav = useNavigate();
    const [b, setB] = useState(null);
    const [err, setErr] = useState(null);
    const { hostId } = useTenant();
    useEffect(() => { planApi.minuteBalance(hostId).then((r) => setB(r.balance)).catch(setErr); }, [hostId]);
    const packs = [...(b?.packs || [])].sort((a, z) => z.loadedAt - a.loadedAt);
    return (
        <div>
            <button type="button" onClick={() => nav('/dashboard')} className="mb-3 inline-flex items-center gap-1 text-xs text-ink-500 hover:text-ink-900"><ChevronLeft size={14} />{t('minutePack.records.back')}</button>
            <PageHeader eyebrow="minutes · records" title={t('minutePack.records.title')} description={t('minutePack.records.desc')} />
            {err && <p className="mb-4 text-sm text-danger-500">{String(err.message || err)}</p>}
            <div className="mb-3 flex items-baseline gap-3 flex-wrap">
                <h2 className="text-sm font-semibold text-ink-900">{t('minutePack.records.section')}</h2>
                {b && <span className="text-xs text-ink-500">{t('minutePack.records.meta', { left: num(Math.max(0, b.packBalance || 0)) })}</span>}
            </div>
            <Card padded={false} className="overflow-x-auto mb-4">
                <table className="w-full min-w-[720px] text-sm">
                    <thead>
                        <tr className="text-left text-xs text-ink-400 border-b border-cream-200">
                            {['loaded', 'source', 'minutes', 'left', 'expiry', 'status'].map((k) => (
                                <th key={k} className={`px-4 py-2.5 font-normal ${['minutes', 'left'].includes(k) ? 'text-right' : ''}`}>{t(`minutePack.records.col.${k}`)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {!b ? (
                            <tr><td colSpan={6} className="px-4 py-10 text-center text-xs text-ink-400">Loading…</td></tr>
                        ) : packs.length === 0 ? (
                            <tr><td colSpan={6} className="px-4 py-10 text-center text-xs text-ink-400 italic">{t('minutePack.records.empty')}</td></tr>
                        ) : packs.map((p) => {
                            const d = daysUntil(p.expiryDay);
                            const soon = !p.expired && p.remaining > 0 && d <= SOON_DAYS;
                            return (
                                <tr key={p.id} className={`border-b border-cream-100 ${p.expired ? 'text-ink-400' : 'text-ink-900'}`}>
                                    <td className="px-4 py-3 tnum">{new Date(p.loadedAt + 8 * 3600 * 1000).toISOString().slice(0, 10)}</td>
                                    <td className="px-4 py-3">{t('minutePack.records.source')}<div className="font-mono text-[11px] text-ink-400">{p.orderNo || p.id}</div></td>
                                    <td className="px-4 py-3 text-right tnum">{num(p.minutes)}</td>
                                    <td className={`px-4 py-3 text-right tnum ${soon ? 'text-warn-500 font-semibold' : ''}`}>
                                        {p.expired ? <>0 <span className="text-[11px]">{t('minutePack.records.lapsed', { minutes: num(p.remaining) })}</span></> : num(p.remaining)}
                                    </td>
                                    <td className={`px-4 py-3 tnum ${soon ? 'text-warn-500 font-semibold' : ''}`}>{p.expiryDay}</td>
                                    <td className="px-4 py-3">
                                        {p.expired ? <span className="px-2 py-0.5 rounded bg-cream-200 text-ink-500 text-[11px]">{t('minutePack.records.st.expired')}</span>
                                            : soon ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-warn-100 text-warn-500 text-[11px]"><AlertTriangle size={11} />{t('minutePack.records.st.soon', { days: d, minutes: num(p.remaining) })}</span>
                                                : p.remaining <= 0 ? <span className="px-2 py-0.5 rounded bg-cream-200 text-ink-500 text-[11px]">{t('minutePack.records.st.usedUp')}</span>
                                                    : <span className="px-2 py-0.5 rounded bg-success-500/15 text-success-500 text-[11px]">{t('minutePack.records.st.ok')}</span>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </Card>
            <MinuteRuleNotes />
        </div>
    );
}
